import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Loader2, Printer, ScanLine, X, RotateCcw, Square, Rows2, Grid2x2, RectangleVertical, RectangleHorizontal, LayoutTemplate, QrCode, Building2 } from "lucide-react";
import { api } from "@/lib/api";
import { brandStyle } from "@/lib/brand";
import { BrandLogo } from "@/components/BrandLogo";
import { PoweredBy } from "@/components/VinconexMark";
import { Button } from "@/components/ui/button";

const PER_PAGE = [
  { value: 1, label: "Single", icon: Square },
  { value: 2, label: "Double", icon: Rows2 },
  { value: 4, label: "4 per page", icon: Grid2x2 },
];
const ORIENT = [
  { value: "portrait", label: "Vertical", icon: RectangleVertical },
  { value: "landscape", label: "Horizontal", icon: RectangleHorizontal },
];

const STYLES = [
  { value: "full", label: "Full details", icon: LayoutTemplate },
  { value: "qr", label: "QR only", icon: QrCode },
];

function Spec({ label, value }) {
  return (
    <tr className="border-b border-gray-200 last:border-0">
      <td className="py-[0.6cqmin] font-semibold text-gray-600">{label}</td>
      <td className="py-[0.6cqmin] text-right font-mono">{value ?? "—"}</td>
    </tr>
  );
}

function SkipButton({ onSkip, id }) {
  if (!onSkip) return null;
  return (
    <button onClick={onSkip} data-testid={`sticker-toggle-${id}`} title="Skip this sticker"
      className="no-print absolute top-2 right-2 z-10 flex items-center gap-1 rounded-full border border-slate-300 bg-white/90 px-2 py-1 text-[11px] font-semibold text-slate-700 shadow hover:bg-red-50 hover:text-red-600">
      <X className="h-3 w-3" /> Skip
    </button>
  );
}

// QR-only sticker: just the code (SCAN FOR PRICE / Stock / VIN are printed inside the image) with the dealer name.
function QrOnlyCard({ item, dealer, onSkip }) {
  const v = item.vehicle;
  return (
    <div className="sticker-cell relative min-w-0 min-h-0 overflow-hidden flex items-center justify-center" data-testid={`sticker-${v.id}`}>
      <SkipButton onSkip={onSkip} id={v.id} />
      <div className="flex flex-col items-center justify-center text-center bg-white text-black" style={brandStyle(dealer?.accent_color)}>
        <div className="text-[4cqmin] font-black tracking-tight leading-none mb-[2cqmin] truncate max-w-[90cqmin]" style={{ fontFamily: "Outfit, sans-serif" }}>{dealer?.name || ""}</div>
        <img src={item.qr} alt={`QR code — scan for price, stock #${v.stock_number || ""}, VIN ${v.vin}`} data-testid="sticker-ids" className="h-[78cqmin] w-auto max-w-[92cqmin] object-contain" />
        <PoweredBy light className="mt-[2cqmin] whitespace-nowrap" />
      </div>
    </div>
  );
}

function StickerCard({ item, dealer, onSkip }) {
  const v = item.vehicle;
  return (
    <div className="sticker-cell relative min-w-0 min-h-0 overflow-hidden flex items-center justify-center" data-testid={`sticker-${v.id}`}>
      <SkipButton onSkip={onSkip} id={v.id} />
      <div className="sticker w-full max-h-full flex flex-col bg-white text-black border-[0.5cqmin] border-black/80 rounded-[1.5cqmin] p-[4cqmin]" style={brandStyle(dealer?.accent_color)}>
        <div className="flex items-start justify-between gap-[2cqmin] border-b-[0.8cqmin] border-brand pb-[2cqmin]">
          <div className="min-w-0">
            <div className="text-[5.5cqmin] font-black tracking-tight leading-[1.15] truncate" style={{ fontFamily: "Outfit, sans-serif" }}>{dealer?.name || ""}</div>
            <div className="text-[2.2cqmin] uppercase tracking-[0.3em] text-brand font-bold leading-[1.3] truncate">{dealer?.address || ""}</div>
          </div>
          <div className="text-right text-[2.6cqmin] leading-[1.3] shrink-0">
            <div className="font-bold">STOCK #{v.stock_number || "—"}</div>
            <div>{v.condition}</div>
            {item.store_name && <div className="text-[2cqmin] text-gray-500 truncate max-w-[34cqmin]" data-testid="sticker-store">{item.store_name}</div>}
          </div>
        </div>

        <div className="flex gap-[3cqmin] mt-[3cqmin]">
          <div className="flex-1 min-w-0">
            <div className="text-[6cqmin] font-black leading-[1.1]" style={{ fontFamily: "Outfit, sans-serif" }}>{v.year} {v.make} {v.model}</div>
            <div className="text-[3cqmin] text-gray-700 leading-[1.3] truncate">{v.trim}</div>
            <div className="mt-[2cqmin] inline-flex items-center gap-[1.2cqmin] rounded-[1cqmin] bg-brand text-white px-[2.4cqmin] py-[1.2cqmin] text-[4.2cqmin] font-black uppercase tracking-wide leading-none" data-testid="sticker-scan-for-price">
              <ScanLine className="h-[4cqmin] w-[4cqmin]" /> Scan for price
            </div>
            <table className="w-full text-[2.6cqmin] mt-[3cqmin] leading-[1.3]">
              <tbody>
                <Spec label="VIN" value={v.vin} />
                <Spec label="Exterior" value={v.exterior_color} />
                <Spec label="Drivetrain" value={v.drivetrain} />
                <Spec label="Mileage" value={v.mileage != null ? `${v.mileage.toLocaleString()} mi` : null} />
                <Spec label="Engine" value={v.engine} />
              </tbody>
            </table>
          </div>
          <div className="w-[38cqmin] shrink-0 flex flex-col items-center border-l-[0.4cqmin] border-dashed border-gray-300 pl-[3cqmin]">
            <img src={item.qr} alt={`QR code — scan for price, stock #${v.stock_number || ""}, VIN ${v.vin}`} data-testid="sticker-ids" className="w-full h-auto" />
            <div className="mt-[1cqmin] flex items-center gap-[0.8cqmin] text-brand font-bold text-[2.4cqmin] uppercase tracking-wide">
              <ScanLine className="h-[2.6cqmin] w-[2.6cqmin]" /> Scan me
            </div>
            <p className="text-[2cqmin] text-center text-gray-600 mt-[0.6cqmin] leading-[1.25]">
              Instant pricing{v.condition === "Used" ? ", CARFAX report" : ""} &amp; a specialist on call
            </p>
            <PoweredBy light className="mt-[1.5cqmin] whitespace-nowrap" />
          </div>
        </div>
      </div>
    </div>
  );
}

function chunk(arr, n) {
  const out = [];
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
}

const UNASSIGNED = "Unassigned";
const storeOf = (it) => it.store_name || UNASSIGNED;

// Stickers → pages. With grouping on, each store starts on a fresh page so the printed stacks come out pre-sorted.
function buildPages(items, per, group) {
  if (!group) return chunk(items, per).map((stickers) => ({ stickers, store: null }));
  const order = [];
  const byStore = new Map();
  items.forEach((it) => {
    const k = storeOf(it);
    if (!byStore.has(k)) { byStore.set(k, []); order.push(k); }
    byStore.get(k).push(it);
  });
  order.sort((a, b) => (a === UNASSIGNED) - (b === UNASSIGNED) || a.localeCompare(b));
  return order.flatMap((k) => chunk(byStore.get(k), per).map((stickers, i) => ({ stickers, store: k, first: i === 0 })));
}

// Per-store counts for the toolbar summary
function summarize(items) {
  const m = new Map();
  items.forEach((it) => m.set(storeOf(it), (m.get(storeOf(it)) || 0) + 1));
  return [...m.entries()].sort((a, b) => (a[0] === UNASSIGNED) - (b[0] === UNASSIGNED) || b[1] - a[1]);
}

export default function StickerSheet() {
  const { vehicleId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [dealer, setDealer] = useState(null);
  const [state, setState] = useState("loading");
  const [excluded, setExcluded] = useState(() => new Set());
  const [marked, setMarked] = useState(false);

  // Printing marks every included sticker as printed so the dashboard can show what still needs one
  const print = () => {
    const ids = included.map((it) => it.vehicle.id);
    if (ids.length) api.post("/vehicles/mark-printed", { ids }).then(() => setMarked(true)).catch(() => {});
    window.print();
  };

  const per = [1, 2, 4].includes(Number(searchParams.get("per"))) ? Number(searchParams.get("per")) : 1;
  const orient = searchParams.get("orient") === "landscape" ? "landscape" : "portrait";
  const style = searchParams.get("style") === "qr" ? "qr" : "full";
  const storeFilter = searchParams.get("store") || "";          // "" all | store id | "unassigned"
  const group = searchParams.get("group") !== "0";              // group pages by store (on by default when stores exist)
  const stores = dealer?.stores || [];
  const Card = style === "qr" ? QrOnlyCard : StickerCard;
  const setLayout = (patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, val]) => (val === "" || val == null ? next.delete(k) : next.set(k, String(val))));
    setSearchParams(next, { replace: true });
  };

  const toggle = (id) =>
    setExcluded((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  const included = useMemo(() => items.filter((it) => !excluded.has(it.vehicle.id)), [items, excluded]);
  const skipped = useMemo(() => items.filter((it) => excluded.has(it.vehicle.id)), [items, excluded]);
  const grouping = stores.length > 0 && group && !storeFilter;
  const pages = useMemo(() => buildPages(included, per, grouping), [included, per, grouping]);
  const summary = useMemo(() => summarize(included), [included]);

  useEffect(() => {
    async function load() {
      try {
        const dRes = await api.get("/dealer");
        setDealer(dRes.data);
        const storeQ = storeFilter ? `&store=${encodeURIComponent(storeFilter)}` : "";
        if (vehicleId === "all") {
          const { data } = await api.get(`/inventory/qrsheet?limit=1000${storeQ}`);
          setItems(data);
        } else if (vehicleId === "unprinted") {
          const { data } = await api.get(`/inventory/qrsheet?printed=false&limit=1000${storeQ}`);
          setItems(data);
        } else if (vehicleId === "selected") {
          const ids = searchParams.get("ids") || "";
          const { data } = await api.get(`/inventory/qrsheet?ids=${encodeURIComponent(ids)}&limit=1000`);
          setItems(storeFilter ? data.filter((it) => (storeFilter === "unassigned" ? !it.vehicle.store_id : it.vehicle.store_id === storeFilter)) : data);
        } else {
          const [qr, pub] = await Promise.all([api.get(`/vehicles/${vehicleId}/qr`), api.get(`/vehicles/${vehicleId}`)]);
          setItems([{ vehicle: pub.data, qr: qr.data.qr, url: qr.data.url }]);
        }
        setState("ok");
      } catch {
        setState("error");
      }
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId, storeFilter]);

  if (state === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0B0E]">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }
  if (state === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0B0E] text-slate-300">
        Unable to load. Make sure you are signed in to the dashboard.
      </div>
    );
  }

  const segBtn = (active) =>
    `h-9 px-3 rounded-md text-xs font-semibold inline-flex items-center gap-1.5 border transition-colors ${
      active ? "bg-[#0A8CF2] border-[#0A8CF2] text-white" : "border-slate-700 bg-slate-900/50 text-slate-300 hover:bg-slate-800"
    }`;

  return (
    <div className="min-h-screen bg-[#0A0B0E] py-8">
      <style>{`@page { size: letter ${orient}; margin: 10mm; }`}</style>
      <div className="no-print max-w-[900px] mx-auto px-4 mb-6 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <BrandLogo dealer={dealer} />
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1" role="group" aria-label="Sticker style" data-testid="layout-style">
            {STYLES.map((o) => (
              <button key={o.value} onClick={() => setLayout({ style: o.value })} data-testid={`layout-style-${o.value}`} aria-pressed={style === o.value} className={segBtn(style === o.value)}>
                <o.icon className="h-3.5 w-3.5" /> {o.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1" role="group" aria-label="Stickers per page" data-testid="layout-per-page">
            {PER_PAGE.map((o) => (
              <button key={o.value} onClick={() => setLayout({ per: o.value })} data-testid={`layout-per-${o.value}`} aria-pressed={per === o.value} className={segBtn(per === o.value)}>
                <o.icon className="h-3.5 w-3.5" /> {o.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1" role="group" aria-label="Paper orientation" data-testid="layout-orientation">
            {ORIENT.map((o) => (
              <button key={o.value} onClick={() => setLayout({ orient: o.value })} data-testid={`layout-orient-${o.value}`} aria-pressed={orient === o.value} className={segBtn(orient === o.value)}>
                <o.icon className="h-3.5 w-3.5" /> {o.label}
              </button>
            ))}
          </div>
          <span className="font-mono-tech text-xs text-slate-400" data-testid="sticker-print-count">
            {included.length} of {items.length} · {pages.length} page{pages.length === 1 ? "" : "s"}
          </span>
          <Button onClick={print} disabled={included.length === 0} data-testid="print-stickers-btn" className="bg-brand hover:bg-brand/90 font-bold">
            <Printer className="mr-2 h-4 w-4" /> Print {included.length > 1 ? `${included.length} stickers` : "sticker"}
          </Button>
        </div>
      </div>
      {marked && (
        <p className="no-print max-w-[900px] mx-auto px-4 -mt-3 mb-3 text-xs text-emerald-300" data-testid="sticker-marked-printed">
          Marked {included.length} vehicle{included.length === 1 ? "" : "s"} as printed — they'll drop off the “Not printed” list in Inventory.
        </p>
      )}
      {stores.length > 0 && items.length > 1 && (
        <div className="no-print max-w-[900px] mx-auto px-4 mb-4 flex flex-col md:flex-row md:items-center gap-3 rounded-xl border border-[#2A2E3D] bg-[#12141C] p-3" data-testid="sticker-store-bar">
          <div className="flex items-center gap-1 flex-wrap" role="group" aria-label="Store" data-testid="sticker-store-filter">
            <Building2 className="h-3.5 w-3.5 text-slate-400 mr-1" />
            <button onClick={() => setLayout({ store: "" })} data-testid="sticker-store-all" aria-pressed={!storeFilter} className={segBtn(!storeFilter)}>All stores</button>
            {stores.map((s) => (
              <button key={s.id} onClick={() => setLayout({ store: s.id })} data-testid={`sticker-store-${s.id}`} aria-pressed={storeFilter === s.id} className={segBtn(storeFilter === s.id)}>{s.name}</button>
            ))}
            <button onClick={() => setLayout({ store: "unassigned" })} data-testid="sticker-store-unassigned" aria-pressed={storeFilter === "unassigned"} className={segBtn(storeFilter === "unassigned")}>Unassigned</button>
          </div>
          <div className="md:ml-auto flex items-center gap-3 flex-wrap">
            <span className="font-mono-tech text-xs text-slate-400" data-testid="sticker-store-summary">
              {summary.map(([name, n]) => `${name} ${n}`).join(" · ") || "—"}
            </span>
            {!storeFilter && (
              <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                <input type="checkbox" data-testid="sticker-group-toggle" checked={group} onChange={(e) => setLayout({ group: e.target.checked ? "" : "0" })} className="h-3.5 w-3.5 accent-[#0A8CF2]" />
                New page per store
              </label>
            )}
          </div>
        </div>
      )}
      <p className="no-print max-w-[900px] mx-auto px-4 mb-5 text-xs text-slate-500">
        {vehicleId === "unprinted" ? <><b className="text-slate-300">Not-yet-printed stickers only.</b>{" "}</> : null}
        {storeFilter ? <><b className="text-slate-300">{storeFilter === "unassigned" ? "Unassigned units only" : `${stores.find((s) => s.id === storeFilter)?.name || "Store"} only`}.</b>{" "}</> : grouping ? <>Grouped by store — each store starts on a new page.{" "}</> : null}
        No prices on paper — every sticker says <b className="text-slate-300">Scan for price</b>. Standard Letter paper (8.5 × 11 in).{" "}
        {style === "qr" ? "QR code only. " : "Full vehicle details. "}
        {per === 1 ? "One sticker per page." : per === 2 ? "Two stickers per page." : "Four stickers per page."}{" "}
        {orient === "portrait" ? "Vertical (portrait) sheet." : "Horizontal (landscape) sheet."}{items.length > 1 ? " Hit Skip on any sticker to leave it out." : ""}
      </p>

      <div className="px-4 space-y-8" data-testid="sticker-pages">
        {pages.map((page, i) => (
          <div key={i}>
            {page.first && (
              <div className="no-print mx-auto w-full max-w-[860px] mb-2 flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400 font-mono-tech" data-testid={`sticker-store-divider-${i + 1}`}>
                <Building2 className="h-3.5 w-3.5" /> {page.store} · {summary.find(([n]) => n === page.store)?.[1] || 0} sticker{(summary.find(([n]) => n === page.store)?.[1] || 0) === 1 ? "" : "s"}
              </div>
            )}
            <div className={`sheet sheet-${per} sheet-${orient} mx-auto w-full max-w-[860px] rounded-lg shadow-2xl p-[5mm]`} data-testid={`sticker-page-${i + 1}`}>
              {page.stickers.map((it) => (
                <Card key={it.vehicle.id} item={it} dealer={dealer} onSkip={items.length > 1 ? () => toggle(it.vehicle.id) : null} />
              ))}
            </div>
          </div>
        ))}
        {pages.length === 0 && (
          <p className="text-center text-slate-500 py-16" data-testid="sticker-empty">Every sticker is skipped — add one back below to print.</p>
        )}
      </div>

      {skipped.length > 0 && (
        <div className="no-print max-w-[860px] mx-auto px-4 mt-8" data-testid="sticker-skipped-tray">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-mono-tech mb-2">Skipped ({skipped.length})</div>
          <div className="flex flex-wrap gap-2">
            {skipped.map((it) => (
              <button key={it.vehicle.id} onClick={() => toggle(it.vehicle.id)} data-testid={`sticker-restore-${it.vehicle.id}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 bg-slate-900/60 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800">
                <RotateCcw className="h-3 w-3" /> {it.vehicle.year} {it.vehicle.make} {it.vehicle.model} · {it.vehicle.stock_number || it.vehicle.vin?.slice(-6)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
